// Product Control Plane — review the diff, then publish.
//
// Two things this screen refuses to do.
//
// It refuses to be a JSON diff. Raw JSON is available as a secondary view and
// nothing more: a diff an operator has to read as JSON is a diff nobody reads,
// and the field that matters is usually one word inside a 200-line object.
//
// It refuses to let publish be one click. The confirmation restates the lane by
// name, lists what appears and disappears for real devices, and on PRODUCT
// requires the product id typed out.

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createProposal,
  getDraft,
  getProduct,
  publish,
  validateDraft,
  type ProductDetailResponse,
  type ValidationReport,
} from "../controlPlane/api";
import type { ControlPlaneDocument, Lane } from "../controlPlane/contract";
import { diffDocuments, renderValue, summarize, type DocumentDiff } from "../controlPlane/diff";
import { classifyControlPlaneError, failureLine } from "../controlPlane/errors";
import { GovernedAction } from "../controlPlane/GovernedAction";
import { laneHost, laneLabel, useControlPlaneTheme } from "../controlPlane/theme";
import {
  Btn,
  Card,
  CpHeader,
  DiffGlyph,
  Empty,
  ErrorNote,
  Hash,
  LaneChip,
} from "../controlPlane/ui";

/** Page-level read failures. Every REFUSAL of the publish itself is classified
 *  by `GovernedAction`; this is for the loads that precede it. */
function describeError(e: unknown): string {
  return failureLine(classifyControlPlaneError(e));
}

function CountTile({
  label,
  n,
  tone,
}: {
  label: string;
  n: number;
  tone: "ok" | "danger" | "accent" | "link";
}) {
  return (
    <Card accent={tone === "link" ? undefined : tone}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: `var(--${tone})`,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, marginTop: 4, color: `var(--${tone})` }}>{n}</div>
    </Card>
  );
}

export function ControlPlaneReview() {
  const { productId = "" } = useParams();
  const navigate = useNavigate();
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const [detail, setDetail] = useState<ProductDetailResponse | null>(null);
  const [draftDoc, setDraftDoc] = useState<ControlPlaneDocument | null>(null);
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showJson, setShowJson] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, dr] = await Promise.all([getProduct(productId), getDraft(productId)]);
      setDetail(d);
      setDraftDoc(dr.document);
      setReport(await validateDraft(productId).catch(() => null));
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const lane: Lane | null = detail?.lane ?? null;
  const published = detail?.published ?? null;

  const diff: DocumentDiff | null = useMemo(() => {
    if (!published || !draftDoc) return null;
    return diffDocuments(published.document, draftDoc);
  }, [published, draftDoc]);

  // Visibility changes are grouped by lane so "disappears on PRODUCT" never
  // reads as "disappears everywhere".
  const visibilityByLane = useMemo(() => {
    const m = new Map<Lane, DocumentDiff["visibility"]>();
    for (const v of diff?.visibility ?? []) {
      const list = m.get(v.lane) ?? [];
      list.push(v);
      m.set(v.lane, list);
    }
    return m;
  }, [diff]);

  // ─── What the proposal will commit to ──────────────────────────────────
  //
  // The server derives the publish target from `loadDraft(...).documentHash`,
  // which is the SAME value it reports as `draft.documentHash` on the product
  // read. Using the validation report's hash instead would be wrong the moment
  // the operator validated an unsaved candidate: the approval would name bytes
  // that are not what a publish would take.
  const draftHash = detail?.draft?.documentHash ?? null;

  // The preconditions that are NOT about authority. They were here before the
  // approval flow and are unchanged; the governed action ANDs its own gate on
  // top rather than replacing them.
  const contentBlockedReason =
    detail === null
      ? "Loading."
      : detail.publishedError !== null && published === null && detail.current !== null
        ? "The live revision cannot be read, so there is no baseline to publish against."
        : diff === null
          ? "No revision has been published yet — this product needs its genesis bootstrap first."
          : diff.identical
            ? "The draft is identical to the live revision. Publishing an unchanged document is refused."
            : report !== null && !report.valid
              ? "The draft does not validate. Fix the issues above first."
              : draftHash === null
                ? "This product has no draft to publish."
                : null;

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <CpHeader
        breadcrumb={
          <>
            <Link to="/control-plane">Control Plane</Link>
            {" / "}
            <Link to={`/control-plane/${productId}`}>{productId}</Link>
          </>
        }
        title="Review changes"
        subtitle={
          published
            ? `draft vs ${published.revisionId}`
            : "draft — nothing published yet"
        }
        actions={
          <>
            <LaneChip lane={lane} host={laneHost(lane)} />
            <button type="button" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </button>
            <Btn onClick={() => setShowJson((v) => !v)}>
              {showJson ? "Hide raw JSON" : "Raw JSON"}
            </Btn>
          </>
        }
      />

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      {/* The live revision exists but cannot be READ. Distinct from "nothing
          has been published", and the screen used to render the second when
          the first was true — steering the operator toward a bootstrap that
          would be refused with 409, on a product whose integrity check is
          failing. */}
      {detail?.publishedError && published === null && detail.current !== null && (
        <ErrorNote>
          The live revision <b>{detail.current.revisionId}</b> could not be read:{" "}
          <code>{detail.publishedError}</code>. There is no baseline to diff
          against and none to publish over. Consumers are being served a
          fail-closed 503 rather than a document this service cannot vouch for.
        </ErrorNote>
      )}

      {report && !report.valid && (
        <Card accent="danger" title="The draft does not validate — publish is blocked">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {report.issues.map((i, n) => (
              <li key={`${i.path}-${n}`}>
                <b>{i.code}</b> — {i.message}
                <div style={{ color: "var(--fg-dim)", fontSize: 11 }}>{i.path}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {loading ? (
        <Empty>Loading…</Empty>
      ) : diff === null ? (
        detail?.publishedError && detail.current !== null ? (
          <Card accent="danger" title="The live revision cannot be read">
            A revision IS published — the pointer names{" "}
            <b>{detail.current.revisionId}</b> — but this service refused to
            serve it (<code>{detail.publishedError}</code>). That is not the
            same as having nothing published, and bootstrapping is not the
            remedy: it would be refused with a conflict.
          </Card>
        ) : (
          <Card accent="warn" title="Nothing to compare">
            No revision has been published, so there is no baseline to diff
            against. Bootstrap the first revision from the product page.
          </Card>
        )
      ) : diff.identical ? (
        <Card accent="ok" title="No changes">
          The draft is identical to <b>{published?.revisionId}</b>. Publishing an
          unchanged document is refused rather than minting a duplicate revision
          — a revision list where half the entries are byte-identical is a
          revision list nobody reads.
        </Card>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 12,
            }}
          >
            <CountTile label="Added" n={diff.added.length} tone="ok" />
            <CountTile label="Removed" n={diff.removed.length} tone="danger" />
            <CountTile label="Modified" n={diff.modified.length} tone="accent" />
            <CountTile label="Reordered" n={diff.reordered.length} tone="link" />
          </div>

          {visibilityByLane.size > 0 && (
            <Card
              accent="warn"
              title="Visibility impact"
              note="rows whose renderability changes — the class of change people do not intend"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[...visibilityByLane.entries()].map(([l, list]) => (
                  <div key={l}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {laneLabel(l)}
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {list.map((v) => (
                        <li key={`${l}-${v.itemId}`}>
                          <b>{v.itemId}</b>{" "}
                          {v.isVisible ? (
                            <span style={{ color: "var(--ok)" }}>starts rendering</span>
                          ) : (
                            <span style={{ color: "var(--danger)" }}>
                              stops rendering
                              {v.reason ? ` — ${v.reason}` : ""}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card title="Field-level changes" style={{ padding: 0 }}>
            <div style={{ padding: "6px 16px 14px", display: "flex", flexDirection: "column" }}>
              {[...diff.added, ...diff.removed, ...diff.modified, ...diff.reordered].map(
                (change, n) => (
                  <div
                    key={`${change.kind}-${n}`}
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "9px 0",
                      borderTop: "1px solid var(--border-faint, #20262e)",
                    }}
                  >
                    <DiffGlyph kind={change.kind} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {change.kind === "added" && (
                        <>
                          <b>{change.item.id}</b>{" "}
                          <span style={{ color: "var(--fg-dim)" }}>{change.item.title}</span>
                          <div style={{ color: "var(--fg-dim)", marginTop: 3 }}>
                            {change.item.url} · {change.item.environment}
                            {change.item.minAppVersion
                              ? ` · min ${change.item.minAppVersion}`
                              : ""}
                          </div>
                        </>
                      )}
                      {change.kind === "removed" && (
                        <>
                          <b>{change.item.id}</b>{" "}
                          <span style={{ color: "var(--fg-dim)" }}>{change.item.title}</span>
                          <div style={{ color: "var(--fg-dim)", marginTop: 3 }}>
                            removed from the document
                          </div>
                        </>
                      )}
                      {change.kind === "modified" && (
                        <>
                          <b>{change.after.id}</b>
                          <div
                            style={{
                              marginTop: 5,
                              display: "grid",
                              gridTemplateColumns: "minmax(92px, max-content) 1fr",
                              gap: "4px 10px",
                            }}
                          >
                            {change.changes.map((c) => (
                              <Fragment key={c.field}>
                                <span style={{ color: "var(--fg-dim)" }}>
                                  {c.field}
                                </span>
                                <span>
                                  <span
                                    style={{
                                      color: "var(--danger)",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    {renderValue(c.before)}
                                  </span>
                                  <span style={{ color: "var(--fg-dim)" }}> → </span>
                                  <span style={{ color: "var(--ok)" }}>
                                    {renderValue(c.after)}
                                  </span>
                                </span>
                              </Fragment>
                            ))}
                          </div>
                        </>
                      )}
                      {change.kind === "reordered" && (
                        <>
                          <b>{change.item.id}</b>
                          <span style={{ color: "var(--fg-dim)" }}>
                            {" "}
                            position {change.fromPosition + 1} → {change.toPosition + 1}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Card>

          {showJson && (
            <Card title="Raw JSON" note="secondary view — the semantic diff above is the review surface">
              <pre style={{ maxHeight: 420, overflow: "auto" }}>
                {JSON.stringify(draftDoc, null, 2)}
              </pre>
            </Card>
          )}
        </>
      )}

      {/* ── The governed act ────────────────────────────────────────────
          Publish is no longer a button in the header. It is the last step of
          a cycle — review, request an approval, watch it reach its threshold,
          then execute with the exact proposal — and each step is separately
          refusable, so each one is separately visible. */}
      <GovernedAction
        act={{
          operation: "publish",
          productId,
          lane,
          // The bytes the approval commits to. Sent by nobody: the server
          // derives it from the stored draft at propose time AND again at
          // execute time, and refuses a proposal whose target no longer
          // matches. Shown here so an operator can see WHICH draft they are
          // asking approval for.
          target: draftHash,
        }}
        title={`Publish to ${laneLabel(lane)}`}
        tone={lane === "production" ? "danger" : "warning"}
        executeLabel={`Publish to ${laneLabel(lane)}`}
        blockedReason={contentBlockedReason}
        consequence={
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div>
              {lane === "production" ? (
                <>
                  <b>{laneHost(lane) ?? "this lane"}</b> — every installed Gao
                  Browser and GaoKey build reads this within five minutes.
                </>
              ) : (
                <>
                  <b>{laneHost(lane) ?? "this lane"}</b> — this lane only. No
                  Product device is affected.
                </>
              )}
            </div>
            <div>
              This creates a new immutable revision and moves the live pointer.{" "}
              <b>{published?.revisionId ?? "the current revision"}</b> is kept and
              can be restored in one action.
            </div>
          </div>
        }
        rows={[
          { key: "action", value: "publish" },
          { key: "product", value: productId },
          { key: "environment", value: laneLabel(lane) },
          { key: "current revision", value: published?.revisionId ?? "—" },
          {
            key: "target",
            value: "the stored draft, as the server reads it at execute time",
          },
          { key: "target digest", value: <Hash value={draftHash} /> },
          {
            key: "change",
            value: diff === null ? "—" : summarize(diff),
          },
        ]}
        requireTypedConfirmation={
          lane === "production"
            ? {
                expected: productId,
                label: `Type "${productId}" to confirm a PRODUCT publish`,
              }
            : undefined
        }
        onRequestApproval={() => createProposal(productId, { operation: "publish" })}
        onExecute={async (proposalId) => {
          // BOTH guards travel: the proposal is the authority, and
          // `expectedBaseRevisionId` is the compare-and-set. Neither substitutes
          // for the other — an approval commits to the draft being published,
          // not to what was live when it was minted.
          const r = await publish(productId, published?.revisionId ?? null, proposalId);
          navigate(`/control-plane/${productId}`, {
            state: { published: r.revisionId },
          });
        }}
      >
        {diff !== null && !diff.identical && (
          <div
            style={{
              border: "1px solid var(--border-faint, #20262e)",
              borderRadius: 4,
              padding: 10,
              background: "var(--bg-faint, #11151a)",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <div>{summarize(diff)}</div>
            {diff.visibility
              .filter((v) => !v.isVisible)
              .map((v) => (
                <div key={`c-${v.lane}-${v.itemId}`} style={{ color: "var(--warn)" }}>
                  {v.itemId} stops rendering on {laneLabel(v.lane)}
                  {v.reason ? ` — ${v.reason}` : ""}
                </div>
              ))}
          </div>
        )}
      </GovernedAction>
    </div>
  );
}
